import React, { useState } from 'react';
import { Phone, Mail, MapPin, CheckCircle2, ArrowRight } from 'lucide-react';
import { CONTACT } from '@/constants/testIds';
import { CONTACT_INFO } from '@/lib/content';
import { submitContact } from '@/lib/api';
import SEO from '@/components/SEO';

const initial = { name: '', email: '', phone: '', company: '', subject: '', message: '' };

export default function Contact() {
    const [form, setForm] = useState(initial);
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState('');

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setStatus('loading');
        setError('');
        try {
            await submitContact(form);
            setStatus('success');
            setForm(initial);
        } catch (err) {
            setStatus('error');
            const detail = err?.response?.data?.detail;
            setError(Array.isArray(detail) ? (detail[0]?.msg || 'Validation failed') : (detail || 'Something went wrong. Try again.'));
        }
    };

    return (
        <div data-testid={CONTACT.section} className="pt-32 bg-white">
            <SEO title="Contact" description="Tell us about your next campaign. Call 321-288-1735 or email mike@cfladgroup.com." path="/contact" />
            <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-12">
                <div className="text-xs uppercase tracking-[0.35em] font-bold text-brand-orange mb-5">
                    Get In Touch
                </div>
                <h1 className="font-display font-black text-brand-navy text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95] max-w-5xl">
                    Tell us about<br />
                    <span className="italic font-medium text-brand-orange">your next campaign.</span>
                </h1>
                <p className="text-brand-navy/70 text-lg max-w-2xl mt-8 leading-relaxed">
                    Drop us a line below or call the studio direct — we usually respond within one business day.
                </p>
            </section>

            <section className="bg-brand-cream">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 md:py-16 lg:py-20 grid lg:grid-cols-12 gap-12">
                    {/* Form */}
                    <div className="lg:col-span-8">
                        {status === 'success' ? (
                            <div
                                data-testid={CONTACT.success}
                                className="bg-white border-l-4 border-brand-orange p-10 md:p-14 shadow-[0_20px_60px_-20px_rgba(31,27,77,0.15)]"
                            >
                                <CheckCircle2 className="w-10 h-10 text-brand-orange mb-6" />
                                <h2 className="font-display font-extrabold text-3xl md:text-4xl text-brand-navy mb-3">
                                    Message received.
                                </h2>
                                <p className="text-brand-navy/75 max-w-lg">
                                    Thanks for reaching out — Mike and the team will be in touch within one business day.
                                    For anything urgent, call the studio at{' '}
                                    <a href={CONTACT_INFO.phoneHref} className="text-brand-orange font-semibold hover:underline">
                                        {CONTACT_INFO.phone}
                                    </a>.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setStatus('idle')}
                                    className="mt-8 inline-flex items-center gap-3 bg-brand-navy text-white hover:bg-brand-orange px-6 py-3 text-xs font-bold uppercase tracking-[0.25em] transition-colors"
                                >
                                    Send another message <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        ) : (
                            <form
                                data-testid={CONTACT.form}
                                onSubmit={onSubmit}
                                className="bg-white p-7 md:p-8 lg:p-9 border border-brand-navy/10 space-y-6 shadow-[0_10px_30px_-15px_rgba(31,27,77,0.15)]"
                                noValidate
                            >
                                <div className="grid md:grid-cols-2 gap-6">
                                    <Field label="Your name" name="name" value={form.name} onChange={onChange} required tid={CONTACT.inputName} />
                                    <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required tid={CONTACT.inputEmail} />
                                </div>
                                <div className="grid md:grid-cols-2 gap-6">
                                    <Field label="Phone (optional)" name="phone" value={form.phone} onChange={onChange} tid={CONTACT.inputPhone} />
                                    <Field label="Company / Dealership" name="company" value={form.company} onChange={onChange} tid={CONTACT.inputCompany} />
                                </div>
                                <Field label="Subject" name="subject" value={form.subject} onChange={onChange} tid={CONTACT.inputSubject} />
                                <div>
                                    <label className="block text-[11px] uppercase tracking-[0.3em] font-bold text-brand-orange mb-2">
                                        Tell us about your campaign *
                                    </label>
                                    <textarea
                                        name="message"
                                        rows={6}
                                        required
                                        data-testid={CONTACT.inputMessage}
                                        value={form.message}
                                        onChange={onChange}
                                        className="w-full bg-brand-cream border border-brand-navy/15 text-brand-navy placeholder:text-brand-navy/35 px-5 py-4 focus:outline-none focus:border-brand-orange transition-colors resize-none"
                                        placeholder="Timeline, channels, goals, anything else we should know…"
                                    />
                                </div>

                                {status === 'error' && (
                                    <div
                                        data-testid={CONTACT.error}
                                        className="border border-red-300 bg-red-50 text-red-700 px-5 py-4 text-sm"
                                    >
                                        {error}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={status === 'loading'}
                                    data-testid={CONTACT.submit}
                                    className="inline-flex items-center gap-3 bg-brand-orange hover:bg-brand-navy disabled:opacity-60 text-white px-8 py-4 text-xs font-bold uppercase tracking-[0.25em] transition-colors"
                                >
                                    {status === 'loading' ? 'Sending…' : 'Send Message'}
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </form>
                        )}
                    </div>

                    {/* Sidebar */}
                    <aside className="lg:col-span-4">
                        <div className="bg-brand-navy text-white p-7 md:p-8 lg:p-9">
                            <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-orange mb-6">
                                Studio Direct
                            </div>
                            <ul className="space-y-7">
                                <li className="flex gap-4">
                                    <span className="inline-flex h-10 w-10 items-center justify-center border border-white/20 text-brand-orange shrink-0">
                                        <Phone className="w-4 h-4" />
                                    </span>
                                    <div>
                                        <div className="text-[11px] uppercase tracking-[0.25em] text-white/45 mb-1">Phone</div>
                                        <a href={CONTACT_INFO.phoneHref} className="text-white text-lg font-display hover:text-brand-orange">
                                            {CONTACT_INFO.phone}
                                        </a>
                                    </div>
                                </li>
                                <li className="flex gap-4">
                                    <span className="inline-flex h-10 w-10 items-center justify-center border border-white/20 text-brand-orange shrink-0">
                                        <Mail className="w-4 h-4" />
                                    </span>
                                    <div>
                                        <div className="text-[11px] uppercase tracking-[0.25em] text-white/45 mb-1">Email</div>
                                        <a href={CONTACT_INFO.emailHref} className="text-white hover:text-brand-orange break-all">
                                            {CONTACT_INFO.email}
                                        </a>
                                    </div>
                                </li>
                                <li className="flex gap-4">
                                    <span className="inline-flex h-10 w-10 items-center justify-center border border-white/20 text-brand-orange shrink-0">
                                        <MapPin className="w-4 h-4" />
                                    </span>
                                    <div>
                                        <div className="text-[11px] uppercase tracking-[0.25em] text-white/45 mb-1">Studio</div>
                                        <p className="text-white/85 leading-relaxed">
                                            {CONTACT_INFO.address1}<br />
                                            {CONTACT_INFO.address2}<br />
                                            {CONTACT_INFO.address3}
                                        </p>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </aside>
                </div>
            </section>
        </div>
    );
}

function Field({ label, name, type = 'text', value, onChange, required, tid }) {
    return (
        <div>
            <label className="block text-[11px] uppercase tracking-[0.3em] font-bold text-brand-orange mb-2">
                {label} {required && '*'}
            </label>
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                required={required}
                data-testid={tid}
                className="w-full bg-brand-cream border border-brand-navy/15 text-brand-navy placeholder:text-brand-navy/35 px-5 py-4 focus:outline-none focus:border-brand-orange transition-colors"
            />
        </div>
    );
}
